import { Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  useCreateInvoice,
  useJobInvoices,
  useInvoiceContents,
  useUpdateInvoiceContents,
} from '@/hooks/useInvoices';
import { useBreakdown, breakdownKeys } from '@/hooks/useBreakdown';
import { useSetLineItemItemized } from '@/hooks/useInvoicePreview';
import { useToast } from '@/hooks/use-toast';
import { useSystemSettings } from '@/hooks/useSettings';
import { formatCurrency, cn } from '@/lib/utils';
import type { Invoice } from '@jobos/shared';
import { PaymentTermsField, DEFAULT_PAYMENT_TERMS } from '@/components/billing/PaymentTermsField';
import { CashInvoiceField } from '@/components/billing/CashInvoiceField';
import { LineItemPickerGroup } from './LineItemPicker';
import { usePartialInvoiceForm } from './usePartialInvoiceForm';

interface PartialInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobCode: string;
  jobDescription: string | null;
  /**
   * Fired once the invoice exists. Optional, and only callers outside the job
   * page pass it — see FinalInvoiceDialog for why this is a prop rather than
   * extra invalidation inside useCreateInvoice.
   */
  onCreated?: (invoice: Invoice) => void;
  /**
   * EDIT MODE: the invoice whose content is being replaced. Absent means a new
   * invoice is being created.
   *
   * The row, not an id, so the title can name it before the contents fetch
   * lands — the alternative is a dialog that changes shape under the user a
   * moment after it opens.
   */
  invoice?: Invoice;
}

export function PartialInvoiceDialog({
  open,
  onOpenChange,
  jobId,
  jobCode,
  jobDescription,
  onCreated,
  invoice,
}: PartialInvoiceDialogProps) {
  const { data: settings } = useSystemSettings();
  const template = settings?.partialInvoiceDescriptionTemplate
    ?? 'Partial payment for {jobCode} — {jobDescription}';
  const defaultDescription = template
    .replace('{jobCode}', jobCode)
    .replace('{jobDescription}', jobDescription ?? '');

  // The presence of a row IS the mode: there is nothing to edit without one.
  const isEdit = invoice != null;
  // Gated on `open` as well, so a dialog that is mounted but closed holds no
  // request open — and so the refetch on reopen reseeds a form that close()
  // wiped.
  const { data: contents } = useInvoiceContents(isEdit && open ? invoice.id : undefined);
  const updateContents = useUpdateInvoiceContents(jobId);

  const form = usePartialInvoiceForm({ jobId, defaultDescription, contents, open });
  const { mode, amount, description, selectedIds, isCash, paymentTerms } = form;

  const createInvoice = useCreateInvoice(jobId);
  const { data: breakdown } = useBreakdown(jobId);
  const { data: invoices } = useJobInvoices(jobId);
  const setItemized = useSetLineItemItemized(jobId, breakdown?.documentId ?? undefined);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Pickable lines = loose lines + every project's nested lines.
  const allLines = [
    ...(breakdown?.lineItems ?? []),
    ...(breakdown?.projects ?? []).flatMap((p) => p.lineItems),
  ];
  const selectedTotal = allLines
    .filter((li) => selectedIds.includes(li.id))
    .reduce((sum, li) => sum + li.totalCost, 0);

  const handleToggleItemized = (lineItemId: string, current: boolean) => {
    const next = !current;
    // Optimistically flip the switch in the cached breakdown so it updates
    // instantly, instead of waiting for the PATCH + a full breakdown refetch
    // (which made the toggle feel laggy by a couple of seconds).
    const patchLine = <T extends { id: string; itemized: boolean }>(li: T): T =>
      li.id === lineItemId ? { ...li, itemized: next } : li;
    queryClient.setQueryData(breakdownKeys.detail(jobId), (old: typeof breakdown) => {
      if (!old) return old;
      return {
        ...old,
        lineItems: old.lineItems.map(patchLine),
        projects: old.projects.map((p) => ({ ...p, lineItems: p.lineItems.map(patchLine) })),
      };
    });
    // Fire the PATCH in the background; on failure, refetch to roll back the
    // optimistic flip. The mutation hook surfaces the error toast.
    setItemized.mutate(
      { lineItemId, itemized: next },
      {
        onError: () => {
          queryClient.invalidateQueries({ queryKey: breakdownKeys.detail(jobId) });
        },
      },
    );
  };

  const jobTotal = breakdown?.totalCost ?? 0;
  const paymentsReceived = (invoices ?? [])
    .filter((inv) => inv.status === 'paid' && inv.kind === 'partial')
    .reduce((sum, inv) => sum + inv.amount, 0);
  const balanceRemaining = jobTotal - paymentsReceived;

  const parsedAmount = parseFloat(amount);
  // NEVER IN EDIT MODE. The remaining balance already counts the invoice being
  // edited, so measuring its own corrected figure against it reads as exceeding
  // a balance the invoice is itself part of.
  const exceedsBalance =
    !isEdit &&
    !isNaN(parsedAmount) &&
    parsedAmount > 0 &&
    balanceRemaining > 0 &&
    parsedAmount > balanceRemaining;

  // One flag for both writes, so the spinner and every disabled control behave
  // the same whichever of the two is in flight.
  const isSubmitting = createInvoice.isPending || updateContents.isPending;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) form.reset();
    onOpenChange(nextOpen);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === 'lineItems') {
      if (selectedIds.length === 0) return;
    } else if (isNaN(parsedAmount) || parsedAmount <= 0) {
      return;
    }

    if (isEdit) {
      try {
        await updateContents.mutateAsync(
          mode === 'lineItems'
            ? {
                invoiceId: invoice.id,
                source: 'lineItems',
                lineItemIds: selectedIds,
                // `|| null`, NOT the raw string: the seed normalises a NULL
                // description to '' because the input can only hold a string,
                // and sending that back would write an empty string over a NULL
                // that nothing asked to change. Both print blank.
                description: description || null,
                paymentTerms,
                isCash,
              }
            : {
                invoiceId: invoice.id,
                source: 'custom',
                amount: parsedAmount,
                description: description || null,
                paymentTerms,
                isCash,
              },
        );
      } catch {
        // The mutation raises its own toast; keep the dialog open so the user
        // can retry or cancel rather than being dropped without feedback.
        return;
      }
      // No toast of our own and no onCreated: the mutation names the invoice it
      // updated, and no row moved out of To Bill.
      handleOpenChange(false);
      return;
    }

    let created;
    try {
      created =
        mode === 'lineItems'
          ? await createInvoice.mutateAsync({
              kind: 'partial',
              source: 'lineItems',
              lineItemIds: selectedIds,
              description,
              isCash,
              paymentTerms,
            })
          : await createInvoice.mutateAsync({
              kind: 'partial',
              amount: parsedAmount,
              description,
              isCash,
              paymentTerms,
            });
    } catch {
      // Create failed — keep the modal open so the user can retry. The error
      // toast is surfaced by the mutation hook.
      return;
    }

    // Moves the row out of To Bill.
    onCreated?.(created);

    // No PDF is saved on create — the invoice row's own Download / View
    // actions are how the user gets the file when they actually want it.
    toast({ title: 'Partial invoice created' });
    handleOpenChange(false);
  };

  // UNCHANGED MEANS NOTHING WOULD CHANGE — IN CUSTOM MODE ONLY.
  //
  // In lineItems mode, saving the same selection is a real operation: it
  // re-resolves every line against today's breakdown, which is what Regenerate
  // used to do for a line-item partial. Disabling it there would delete that
  // capability along with the menu item.
  const unchangedCustom =
    isEdit &&
    contents != null &&
    mode === 'custom' &&
    contents.source === 'custom' &&
    Math.round(parsedAmount * 100) === Math.round(contents.amount * 100) &&
    description === (contents.description ?? '') &&
    isCash === contents.isCash &&
    paymentTerms === (contents.paymentTerms ?? DEFAULT_PAYMENT_TERMS);

  // WOULD SAVING SUPERSEDE THE SENT VERSION? Mirrors the server's rule in
  // `PUT /:id/contents` exactly: a sent invoice leaves a voided copy behind
  // only when the amount moved or the cash flag flipped (which retires its
  // number). A wording change at the same figure does neither and is written
  // straight into the row, keeping its sent date.
  //
  /**
   * Still assembling what the dialog is about to claim.
   *
   * `contents` alone is not enough. A line-item invoice's `nextAmount` is
   * `selectedTotal`, summed from `useBreakdown` — and `contents` can land
   * first, leaving a window where the selection totals 0. Every figure derived
   * from it is wrong in that window, and the worst of them is `willSupersede`,
   * which would announce that saving voids this invoice and sends it back to
   * Drafts when nothing has changed at all.
   */
  const isSeeding =
    isEdit && (!contents || (contents.source === 'lineItems' && !breakdown));

  // IN CENTS, as the server's comparison is. A line-item total is summed from
  // floats here and from per-row cent-rounded figures there, so two readings of
  // the same money disagree in the fifteenth decimal place; compared raw, that
  // fib would threaten to void an invoice nothing changed on.
  const nextAmount =
    mode === 'lineItems' ? selectedTotal : Number.isFinite(parsedAmount) ? parsedAmount : 0;
  const willSupersede =
    isEdit &&
    contents != null &&
    // Not while the breakdown is still arriving — see isSeeding.
    !isSeeding &&
    contents.sentAt != null &&
    (Math.round(nextAmount * 100) !== Math.round(contents.amount * 100) ||
      isCash !== contents.isCash);

  const submitDisabled =
    isSubmitting ||
    // Nothing to replace until the fetch lands, and submitting would send the
    // create defaults the form is still holding.
    isSeeding ||
    unchangedCustom ||
    (mode === 'custom'
      ? exceedsBalance
      : selectedIds.length === 0 || setItemized.isPending);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* SCROLLS RATHER THAN OVERFLOWS. In lineItems mode this dialog was
          already taller than a phone screen, and edit mode adds up to three
          more blocks above the footer — past that point Save is simply
          unreachable, because a Radix dialog locks the page behind it and this
          content had no scroll container of its own. Same pair JobHoursModal
          uses: a viewport cap with its own scroll on desktop, full-screen below
          `sm`. */}
      <DialogContent
        className={cn(
          'max-h-[90vh] overflow-y-auto',
          // `contents.source` ahead of `mode`, so an edit opens at the width it
          // will settle at. `mode` still holds the create default while the
          // seed effect is pending, which made the box start narrow and jump
          // wider a tick later.
          (contents?.source ?? mode) === 'lineItems' ? 'sm:max-w-lg' : 'sm:max-w-md',
        )}
        mobileFullscreen
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit ${invoice.invoiceNumber}` : 'Create Partial Invoice'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="min-w-0 space-y-4">
          {/* LOADING, NOT THE CREATE FORM. `contents` is undefined for the
              first tick of every Edit, and letting the form show through meant
              the dialog opened in Custom-amount mode with an empty figure and
              then snapped to the picker when the fetch landed. Save was always
              disabled meanwhile, so nothing could be submitted wrongly — it was
              simply a flash of the wrong UI, every time.

              The footer stays OUTSIDE this branch so Cancel always works, and
              the form state lives in usePartialInvoiceForm (above, not in here),
              so unmounting the body cannot wipe what the seed wrote. */}
          {isSeeding ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading {invoice.invoiceNumber}…
            </div>
          ) : (
            <>
              {/* Mode toggle: custom amount vs. pick breakdown line items. */}
              <div className="grid grid-cols-2 gap-1 rounded-md bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => form.changeMode('custom')}
                  className={cn(
                    'rounded px-3 py-1.5 text-sm font-medium transition-colors',
                    mode === 'custom' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500',
                  )}
                >
                  Custom amount
                </button>
                <button
                  type="button"
                  onClick={() => form.changeMode('lineItems')}
                  className={cn(
                    'rounded px-3 py-1.5 text-sm font-medium transition-colors',
                    mode === 'lineItems' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500',
                  )}
                >
                  From breakdown items
                </button>
              </div>

              {jobTotal > 0 && (
                <div className="rounded-md bg-gray-50 px-3 py-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Job Total</span>
                    <span className="font-medium">{formatCurrency(jobTotal)}</span>
                  </div>
                  {paymentsReceived > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Payments Received</span>
                      <span className="font-medium text-green-600">-{formatCurrency(paymentsReceived)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-gray-200 mt-1 pt-1">
                    <span className="text-gray-700 font-medium">Balance Remaining</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(balanceRemaining)}</span>
                  </div>
                </div>
              )}

              {mode === 'custom' ? (
                <div className="space-y-1.5">
                  <label htmlFor="partial-amount" className="text-sm font-medium">
                    Amount
                  </label>
                  <input
                    id="partial-amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    value={amount}
                    onChange={(e) => form.setAmount(e.target.value)}
                    placeholder="0.00"
                    className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
                      exceedsBalance ? 'border-red-300 focus-visible:ring-red-500' : 'border-input'
                    }`}
                  />
                  {exceedsBalance && (
                    <p className="text-xs text-red-600">
                      Amount exceeds remaining balance of {formatCurrency(balanceRemaining)}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5">
                  <span className="text-sm font-medium">Breakdown items</span>
                  <div className="overflow-hidden rounded-md border border-input">
                    {/* Column header */}
                    {allLines.length > 0 && (
                      <div className="flex items-center gap-3 border-b bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500">
                        <span className="flex-1 pl-7">Item</span>
                        <span className="w-24 text-right">Amount</span>
                        <span className="w-14 text-center">Itemize</span>
                      </div>
                    )}
                    <div className="max-h-64 divide-y overflow-y-auto">
                      {(breakdown?.lineItems ?? []).length > 0 && (
                        <LineItemPickerGroup
                          lines={breakdown?.lineItems ?? []}
                          selectedIds={selectedIds}
                          onToggleSelected={form.toggleSelected}
                          onToggleItemized={handleToggleItemized}
                          itemizedDisabled={!breakdown?.documentId}
                        />
                      )}
                      {(breakdown?.projects ?? [])
                        .filter((project) => project.lineItems.length > 0)
                        .map((project) => (
                          <div key={project.id}>
                            <p className="bg-gray-50/60 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                              {project.name}
                            </p>
                            <LineItemPickerGroup
                              lines={project.lineItems}
                              selectedIds={selectedIds}
                              onToggleSelected={form.toggleSelected}
                              onToggleItemized={handleToggleItemized}
                              itemizedDisabled={!breakdown?.documentId}
                            />
                          </div>
                        ))}
                      {allLines.length === 0 && (
                        <p className="px-3 py-6 text-center text-sm text-gray-500">
                          This job has no breakdown line items.
                        </p>
                      )}
                    </div>
                    {/* Selected total footer */}
                    {allLines.length > 0 && (
                      <div className="flex items-center justify-between border-t bg-gray-50 px-3 py-2 text-sm">
                        <span className="text-gray-600">
                          {selectedIds.length} selected
                        </span>
                        <span className="font-semibold text-gray-900 tabular-nums">
                          {formatCurrency(selectedTotal)}
                        </span>
                      </div>
                    )}
                    {/* WHAT THE CLIENT WAS TOLD, beside what saving would make it.
                        `contents.amount` is the figure stored on the row, which
                        drifts from the picker's recomputed total whenever the
                        breakdown moved since the invoice was cut. Shown only when
                        the two disagree: when they match it is noise, and when they
                        do not it is the most important number on the screen. */}
                    {isEdit &&
                      contents &&
                      contents.source === 'lineItems' &&
                      Math.round(contents.amount * 100) !== Math.round(selectedTotal * 100) && (
                        <div className="flex items-center justify-between border-t bg-gray-50 px-3 py-1.5 text-xs text-gray-500">
                          <span>Currently invoiced</span>
                          <span className="tabular-nums">{formatCurrency(contents.amount)}</span>
                        </div>
                      )}
                  </div>
                </div>
              )}
              {/* Terms apply to every partial, cash or not — a cash invoice still
                  states when it is due. Outside the custom-mode block for that
                  reason. */}
              <PaymentTermsField
                id="partial-payment-terms"
                value={paymentTerms}
                onChange={form.setPaymentTerms}
                disabled={isSubmitting}
              />
              {/* Description is only meaningful in custom mode, where it IS the
                  single line rendered on the PDF. For line-item partials the
                  selected lines are the content, so the field is hidden (the server
                  defaults invoice.description for record-keeping). */}
              {mode === 'custom' && (
                <div className="space-y-1.5">
                  <label htmlFor="partial-description" className="text-sm font-medium">
                    Description
                  </label>
                  <input
                    id="partial-description"
                    type="text"
                    value={description}
                    onChange={(e) => form.setDescription(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                </div>
              )}
              <CashInvoiceField
                id="partial-cash"
                checked={isCash}
                onChange={form.setCashChoice}
                disabled={isSubmitting}
              />
              {/* INLINE DIVS, NEVER A NESTED <Dialog>. Two Radix modal dialogs
                  open at once marks one of them aria-hidden, which takes it away
                  from the keyboard and from screen readers. */}
              {isEdit && contents?.sentAt && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <p className="font-medium">{invoice.invoiceNumber} has already been sent.</p>
                  <p className="mt-0.5">
                    {willSupersede
                      ? 'Saving keeps the sent version as a voided record under this number and returns this invoice to Drafts, so it can be sent again.'
                      : 'The amount is unchanged, so this corrects the wording in place and keeps its sent date.'}
                  </p>
                </div>
              )}

              {/* SHOWN ON DRAFTS TOO, which the spec did not ask for. When the
                  slot's active breakdown document is not the one the invoice was
                  cut from, EVERY id misses — so the GET returns no line ids at
                  all and the picker opens empty. Silent on a draft, that reads
                  as "this invoice has no lines", and the user's next move is to
                  re-pick lines they believe were never there. The severity and
                  the verb carry the difference instead: amber and "will not be
                  included" on a draft, where nothing was billed; red and
                  "Saving will remove them" on a sent invoice, where those lines
                  went to a client. */}
              {/* PICKER MODE ONLY. These lines are missing from the list below,
                  which is the thing that needs explaining — in Custom amount mode
                  there is no list, and the typed figure replaces every line
                  anyway, so the note would be describing nothing on screen. */}
              {isEdit && contents && mode === 'lineItems' && contents.missingLines.length > 0 && (
                <div
                  className={cn(
                    'rounded-md border px-3 py-2 text-xs',
                    contents.sentAt
                      ? 'border-red-200 bg-red-50 text-red-900'
                      : 'border-amber-200 bg-amber-50 text-amber-900',
                  )}
                >
                  <p className="font-medium">
                    {contents.missingLines.length === 1
                      ? '1 line on this invoice is no longer in the breakdown'
                      : `${contents.missingLines.length} lines on this invoice are no longer in the breakdown`}
                  </p>
                  <ul className="mt-1 list-disc pl-4">
                    {contents.missingLines.map((l, i) => (
                      <li key={i}>{l.description ? `${l.description} — ${l.category}` : l.category}</li>
                    ))}
                  </ul>
                  {/* SENT ONLY. On a draft "saving will leave it off" restates
                      the heading — a line that is not in the breakdown plainly
                      cannot be billed from it. On a sent invoice it is a real
                      consequence rather than a restatement: the client is
                      holding a document that DOES include this line. */}
                  {contents.sentAt && (
                    <p className="mt-1">
                      Saving will remove {contents.missingLines.length === 1 ? 'it' : 'them'} from
                      this invoice.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitDisabled}
              className="bg-blue-900 hover:bg-blue-800 text-white"
            >
              {isSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {isEdit ? 'Save changes' : 'Create Invoice'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
